export function buildDoorItems(formData, choices, group) {
  const id = Number(formData.get('id'));
  const quantity = Number(formData.get('quantity') || 1);
  if (!Number.isSafeInteger(id) || id <= 0 || !Number.isInteger(quantity) || quantity < 1) throw new Error('Invalid product');
  const properties = {};
  for (const [key, value] of formData.entries()) {
    const match = /^properties\[(.*)\]$/.exec(key);
    if (match && typeof value === 'string') properties[match[1]] = value;
  }
  const paid = choices.filter(choice => choice.price > 0);
  if (paid.some(choice => !Number.isSafeInteger(choice.variantId) || choice.variantId <= 0)) throw new Error('Missing layout price');
  if (paid.length) {
    properties._km_layout_group = group;
    properties._km_layout_surcharge = String(paid.reduce((total, choice) => total + choice.price, 0));
    properties._km_layout_count = String(paid.length);
  }
  const parent = { id, quantity, properties };
  const sellingPlan = formData.get('selling_plan');
  if (sellingPlan) parent.selling_plan = String(sellingPlan);
  return [parent, ...paid.map(choice => ({
    id: choice.variantId,
    quantity,
    parent_id: id,
    properties: { 'Dør': String(choice.door), '_km_layout_parent_group': group }
  }))];
}

export function layoutQuantityUpdates(rows, parentKey, quantity) {
  return Object.fromEntries(rows.filter(row => row.key === parentKey || (row.parentKey === parentKey && row.layoutChild)).map(row => [row.key, quantity]));
}

export function getDoorPricing(form) {
  return [...document.querySelectorAll('km-door-pricing')].find(element => element.dataset.formId === form?.id);
}

export function createDoorRequest(form, formData) {
  const pricing = getDoorPricing(form);
  if (!pricing) return null;
  const choices = pricing.choices();
  if (!form.reportValidity()) throw new Error(pricing.dataset.invalidMessage);
  return {
    items: buildDoorItems(formData, choices, crypto.randomUUID()),
    sections: formData.getAll('sections').filter(Boolean).join(','),
    sections_url: window.location.pathname
  };
}

class DoorPricing extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.scope = this.closest('.shopify-section') || document;
    this.addEventListener('change', () => this.update(), { signal: this.controller.signal });
    this.observer = new MutationObserver(() => this.schedule());
    this.observer.observe(this.scope, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-km-product-price'] });
    this.update();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  schedule() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
  }

  choices() {
    const groups = [...this.querySelectorAll('[data-km-door]')];
    return groups.map(group => {
      const selected = group.querySelector('[data-door-division]:checked');
      if (!selected || selected.disabled) throw new Error(this.dataset.invalidMessage);
      const price = Number(selected.dataset.layoutPrice);
      if (!Number.isFinite(price) || price < 0) throw new Error(this.dataset.invalidMessage);
      return { door: Number(group.dataset.kmDoor), price, variantId: Number(selected.dataset.layoutVariant || 0) };
    });
  }

  money(value) {
    return new Intl.NumberFormat(document.documentElement.lang || 'nb-NO', { style: 'currency', currency: this.dataset.currency || 'NOK', maximumFractionDigits: 0 }).format(value / 100);
  }

  update() {
    let surcharge;
    try { surcharge = this.choices().reduce((total, choice) => total + choice.price, 0); }
    catch { return; }
    const prices = [...this.scope.querySelectorAll('[data-km-product-price]')].filter(element => element.dataset.kmProductId === this.dataset.productId);
    const base = Number(prices[0]?.dataset.kmProductPrice || this.dataset.initialPrice);
    const set = (element, value) => { if (element && element.textContent !== value) element.textContent = value; };
    set(this.querySelector('[data-km-layout-total]'), this.money(surcharge));
    set(this.querySelector('[data-km-product-total]'), this.money(base + surcharge));
    for (const container of prices) set(container.querySelector('.price'), this.money(base + surcharge));
  }
}

if (!customElements.get('km-door-pricing')) customElements.define('km-door-pricing', DoorPricing);
